import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LogIssue, LogPage, LogRecord, LogSummary } from '@bidmanager/shared';
import { errorMessage, request } from '../api';

/**
 * Every failure in the instance, in one table.
 *
 * The API records its own exceptions and every service-level warning; the
 * desktop app and both browser consoles report theirs to `POST /logs`. This is
 * the only place they can be read, and the only surface in the product that is
 * deliberately not tenant-scoped — so it is ADMIN-gated on the server and lives
 * in this console rather than in the client portal.
 *
 * Two design choices worth keeping:
 *
 *  - **The list is flushed before it is read.** The API batches writes on a
 *    one-second timer, so a page loaded immediately after a failure would
 *    otherwise show nothing and read as "no errors".
 *  - **Repeats are collapsed by fingerprint in the summary, not in the list.**
 *    An operator needs both "what is broken most" and "what happened, in
 *    order"; merging the two loses the timeline.
 */

const LEVELS = ['ERROR', 'WARN', 'INFO'] as const;
const SOURCES = ['API', 'DESKTOP', 'WEB', 'ADMIN'] as const;
const WINDOWS = [
  { label: 'Last hour', hours: 1 },
  { label: 'Last 24 hours', hours: 24 },
  { label: 'Last 7 days', hours: 24 * 7 },
  { label: 'Last 30 days', hours: 24 * 30 },
] as const;

interface Filters {
  level: string;
  source: string;
  clientId: string;
  q: string;
  hours: number;
  fingerprint: string;
}

const EMPTY: Filters = { level: '', source: '', clientId: '', q: '', hours: 24, fingerprint: '' };

export function Logs(): JSX.Element {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [search, setSearch] = useState('');
  const [pages, setPages] = useState<LogPage[]>([]);
  const [error, setError] = useState<string | null>(null);

  const key = ['admin', 'logs', filters] as const;

  const summary = useQuery({
    queryKey: ['admin', 'logs', 'summary', filters.hours],
    queryFn: () => request<LogSummary>('GET', `/admin/logs/summary?hours=${filters.hours}`),
    refetchInterval: 30_000,
  });

  const logs = useQuery({
    queryKey: key,
    queryFn: async () => {
      // Land whatever is still queued in the API before reading.
      await request<unknown>('POST', '/admin/logs/flush').catch(() => undefined);
      const page = await request<LogPage>('GET', `/admin/logs?${toQuery(filters)}`);
      setPages([page]);
      return page;
    },
  });

  const loadMore = useMutation({
    mutationFn: (cursor: string) =>
      request<LogPage>('GET', `/admin/logs?${toQuery(filters)}&cursor=${encodeURIComponent(cursor)}`),
    onSuccess: (page) => setPages((current) => [...current, page]),
    onError: (caught) => setError(errorMessage(caught)),
  });

  const clients = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: () => request<{ id: string; name: string }[]>('GET', '/admin/clients'),
  });

  const items = pages.flatMap((page) => page.items);
  const nextCursor = pages[pages.length - 1]?.nextCursor ?? null;

  const update = (patch: Partial<Filters>): void => {
    setPages([]);
    setFilters((current) => ({ ...current, ...patch }));
  };

  const refresh = (): void => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['admin', 'logs'] });
  };

  return (
    <>
      <header className="page-head">
        <h1>Logs</h1>
        <p className="muted small" style={{ margin: 0 }}>
          Failures from the API, the bidder desktop app, and both browser consoles. Kept for{' '}
          <code>LOG_RETENTION_DAYS</code> and then dropped by the nightly job — this is
          diagnostics, not the audit trail.
        </p>
      </header>

      {error && <p className="error small">{error}</p>}

      <div className="stack">
        <Summary
          summary={summary.data}
          hours={filters.hours}
          activeFingerprint={filters.fingerprint}
          onPickIssue={(issue) => update({ fingerprint: issue.fingerprint })}
        />

        <section className="card">
          <div className="row" style={{ flexWrap: 'wrap', justifyContent: 'flex-start', gap: 10 }}>
            <select
              value={filters.hours}
              onChange={(e) => update({ hours: Number(e.target.value) })}
              style={{ width: 'auto' }}
            >
              {WINDOWS.map((window) => (
                <option key={window.hours} value={window.hours}>
                  {window.label}
                </option>
              ))}
            </select>

            <select
              value={filters.level}
              onChange={(e) => update({ level: e.target.value })}
              style={{ width: 'auto' }}
            >
              <option value="">All levels</option>
              {LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>

            <select
              value={filters.source}
              onChange={(e) => update({ source: e.target.value })}
              style={{ width: 'auto' }}
            >
              <option value="">All sources</option>
              {SOURCES.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>

            <select
              value={filters.clientId}
              onChange={(e) => update({ clientId: e.target.value })}
              style={{ width: 'auto', maxWidth: 200 }}
            >
              <option value="">All clients</option>
              {(clients.data ?? []).map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                update({ q: search.trim() });
              }}
              style={{ display: 'flex', gap: 6, flex: 1, minWidth: 220 }}
            >
              <input
                value={search}
                placeholder="Search message, code, or scope"
                onChange={(e) => setSearch(e.target.value)}
              />
              <button type="submit" className="sm">
                Search
              </button>
            </form>

            <button className="sm ghost" onClick={refresh} disabled={logs.isFetching}>
              {logs.isFetching ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {(filters.fingerprint || filters.q || filters.level || filters.source ||
            filters.clientId) && (
            <p className="small muted" style={{ marginBottom: 0 }}>
              Filtered
              {filters.fingerprint && <> to one issue (<code>{filters.fingerprint}</code>)</>}
              {filters.q && <> by “{filters.q}”</>}.{' '}
              <button
                className="sm ghost"
                onClick={() => {
                  setSearch('');
                  setPages([]);
                  setFilters({ ...EMPTY, hours: filters.hours });
                }}
              >
                Clear filters
              </button>
            </p>
          )}
        </section>

        <section className="card">
          {logs.isLoading && <p className="muted small">Loading…</p>}
          {logs.isError && <p className="error small">{errorMessage(logs.error)}</p>}
          {logs.data && items.length === 0 && (
            <p className="muted small" style={{ margin: 0 }}>
              Nothing recorded in this window. That is the good outcome — but check{' '}
              <code>LOG_CAPTURE_LEVEL</code> if you expected entries.
            </p>
          )}

          {items.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 150 }}>When</th>
                  <th style={{ width: 70 }}>Level</th>
                  <th style={{ width: 90 }}>Source</th>
                  <th>Message</th>
                  <th style={{ width: 130 }}>Client</th>
                </tr>
              </thead>
              <tbody>
                {items.map((entry) => (
                  <Row
                    key={entry.id}
                    entry={entry}
                    onIsolate={() => update({ fingerprint: entry.fingerprint })}
                  />
                ))}
              </tbody>
            </table>
          )}

          {nextCursor && (
            <button
              className="sm ghost"
              style={{ marginTop: 12 }}
              disabled={loadMore.isPending}
              onClick={() => loadMore.mutate(nextCursor)}
            >
              {loadMore.isPending ? 'Loading…' : 'Load older'}
            </button>
          )}
        </section>

        <Purge onDone={refresh} onError={setError} />
      </div>
    </>
  );
}

function Summary({
  summary,
  hours,
  activeFingerprint,
  onPickIssue,
}: {
  summary?: LogSummary;
  hours: number;
  activeFingerprint: string;
  onPickIssue: (issue: LogIssue) => void;
}): JSX.Element {
  const window = WINDOWS.find((entry) => entry.hours === hours)?.label ?? `${hours}h`;

  return (
    <section className="card">
      <div className="row">
        <strong>{window}</strong>
        <span className="muted small">{summary?.total ?? 0} entries</span>
      </div>

      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 8 }}>
        <Count label="Errors" value={summary?.byLevel?.ERROR ?? 0} tone="error" />
        <Count label="Warnings" value={summary?.byLevel?.WARN ?? 0} tone="warn" />
        <Count label="API" value={summary?.bySource?.API ?? 0} />
        <Count label="Desktop" value={summary?.bySource?.DESKTOP ?? 0} />
        <Count label="Portal" value={summary?.bySource?.WEB ?? 0} />
        <Count label="Admin" value={summary?.bySource?.ADMIN ?? 0} />
      </div>

      {/* A non-zero drop count means the log itself is lossy — usually the
          database being unreachable. Without showing it, an outage looks
          exactly like quiet. */}
      {summary && summary.droppedSinceBoot > 0 && (
        <p className="warn small" style={{ marginBottom: 0 }}>
          {summary.droppedSinceBoot} entr{summary.droppedSinceBoot === 1 ? 'y' : 'ies'} could not
          be written since the API started — the log is incomplete.
        </p>
      )}

      {summary && summary.topIssues.length > 0 && (
        <>
          <p className="muted small" style={{ margin: '14px 0 6px' }}>
            Most frequent issues — one row per distinct failure, not per occurrence.
          </p>
          <table>
            <tbody>
              {summary.topIssues.map((issue) => (
                <tr key={issue.fingerprint}>
                  <td style={{ width: 60 }}>
                    <span className={`badge ${issue.level === 'ERROR' ? 'error' : 'warn'}`}>
                      {issue.count}×
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                      <span className="muted small">{issue.source}</span>
                      {issue.scope && <span className="muted small">· {issue.scope}</span>}
                    </div>
                    <div className="small">{issue.message}</div>
                  </td>
                  <td style={{ width: 110 }} className="muted small">
                    {formatTime(issue.lastSeenAt)}
                  </td>
                  <td style={{ width: 90 }}>
                    <button
                      className="sm ghost"
                      disabled={activeFingerprint === issue.fingerprint}
                      onClick={() => onPickIssue(issue)}
                    >
                      {activeFingerprint === issue.fingerprint ? 'Shown' : 'Show all'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

function Count({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'error' | 'warn';
}): JSX.Element {
  return (
    <div>
      <div className={value > 0 && tone ? tone : undefined} style={{ fontSize: 20 }}>
        {value}
      </div>
      <div className="muted small">{label}</div>
    </div>
  );
}

function Row({ entry, onIsolate }: { entry: LogRecord; onIsolate: () => void }): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <>
      <tr onClick={() => setOpen((current) => !current)} style={{ cursor: 'pointer' }}>
        <td className="muted small">{formatTime(entry.createdAt)}</td>
        <td>
          <span className={`badge ${entry.level === 'ERROR' ? 'error' : 'muted'}`}>
            {entry.level}
          </span>
        </td>
        <td className="muted small">
          {entry.source}
          {entry.scope && <div className="muted small">{entry.scope}</div>}
        </td>
        <td>
          <div style={{ overflowWrap: 'anywhere' }}>{entry.message}</div>
          {entry.code && <span className="muted small">{entry.code}</span>}
        </td>
        <td className="muted small">{entry.clientName ?? (entry.clientId ? '(deleted)' : '—')}</td>
      </tr>

      {open && (
        <tr>
          <td colSpan={5} style={{ background: 'var(--panel-2)' }}>
            <div className="small" style={{ display: 'grid', gap: 8 }}>
              <div className="muted">
                {new Date(entry.createdAt).toLocaleString()}
                {entry.occurredAt && entry.occurredAt !== entry.createdAt && (
                  <> · occurred {new Date(entry.occurredAt).toLocaleString()} on the client</>
                )}
                {entry.appVersion && <> · v{entry.appVersion}</>}
                {entry.userId && <> · user {entry.userId}</>}
              </div>

              {entry.stack && (
                <pre
                  style={{
                    margin: 0,
                    padding: 10,
                    background: 'var(--bg)',
                    borderRadius: 7,
                    overflowX: 'auto',
                    fontSize: 12,
                  }}
                >
                  {entry.stack}
                </pre>
              )}

              {entry.context !== null && entry.context !== undefined && (
                <pre
                  style={{
                    margin: 0,
                    padding: 10,
                    background: 'var(--bg)',
                    borderRadius: 7,
                    overflowX: 'auto',
                    fontSize: 12,
                  }}
                >
                  {JSON.stringify(entry.context, null, 2)}
                </pre>
              )}

              <div className="row" style={{ justifyContent: 'flex-start', gap: 10 }}>
                <span className="muted">
                  Issue <code>{entry.fingerprint}</code>
                </span>
                <button
                  className="sm ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onIsolate();
                  }}
                >
                  Show every occurrence
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Clearing the log is an operator lever, not routine maintenance — the nightly
 * job already trims by age. It exists for the case that motivates it: one
 * defect flooding the table so badly that nothing else can be read.
 */
function Purge({
  onDone,
  onError,
}: {
  onDone: () => void;
  onError: (message: string) => void;
}): JSX.Element {
  const [days, setDays] = useState(30);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<number | null>(null);

  const purge = useMutation({
    mutationFn: () => request<{ deleted: number }>('POST', '/admin/logs/purge', {
      olderThanDays: days,
    }),
    onSuccess: (response) => {
      setResult(response.deleted);
      setConfirming(false);
      onDone();
    },
    onError: (caught) => {
      setConfirming(false);
      onError(errorMessage(caught));
    },
  });

  return (
    <section className="card">
      <div className="row">
        <div>
          <strong>Clear old entries</strong>
          <p className="muted small" style={{ margin: '4px 0 0' }}>
            The nightly job already drops entries past the retention window. Use this to clear a
            flood now. The purge itself is recorded, so the table never just goes quiet.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="number"
            min={0}
            max={3650}
            value={days}
            onChange={(e) => setDays(Math.max(0, Number(e.target.value)))}
            style={{ width: 80 }}
          />
          <span className="muted small">days</span>
          {confirming ? (
            <>
              <button className="danger sm" onClick={() => purge.mutate()} disabled={purge.isPending}>
                {purge.isPending ? 'Deleting…' : days === 0 ? 'Delete everything' : 'Confirm'}
              </button>
              <button className="ghost sm" onClick={() => setConfirming(false)}>
                Cancel
              </button>
            </>
          ) : (
            <button className="sm" onClick={() => setConfirming(true)}>
              Purge
            </button>
          )}
        </div>
      </div>
      {result !== null && (
        <p className="ok small" style={{ marginBottom: 0 }}>
          Deleted {result} entr{result === 1 ? 'y' : 'ies'}.
        </p>
      )}
    </section>
  );
}

function toQuery(filters: Filters): string {
  const params = new URLSearchParams();
  if (filters.level) params.set('level', filters.level);
  if (filters.source) params.set('source', filters.source);
  if (filters.clientId) params.set('clientId', filters.clientId);
  if (filters.q) params.set('q', filters.q);
  if (filters.fingerprint) params.set('fingerprint', filters.fingerprint);
  if (filters.hours) params.set('hours', String(filters.hours));
  params.set('limit', '50');
  return params.toString();
}

/** Short form for the table; the expanded row shows the full local timestamp. */
function formatTime(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return sameDay
    ? date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}
